/* @jsx h */
import { defineComponent, h, PropType } from "vue";
import type { PrintTemplateType } from "@lemmonhq/types";
import { useAuth } from "@app/vue/providers/Auth";

const receiptlineSections = [
  {
    title: "Columns",
    body: `1st
|1st|
1st|2nd
|1st|2nd|
1st| ... |nth
|1st| ... |nth|`,
  },
  {
    title: "Alignment",
    body: `center
|center|
| center |
|left
|left |
left |
right|
| right|
| right`,
  },
  {
    title: "Special characters in text",
    body: `\\  : character escape
|   : column delimiter
{   : property delimiter (start)
}   : property delimiter (end)
-   : horizontal rule
=   : paper cut
~   : space
_   : underline
"   : emphasis
\`   : invert
^   : double width
^^  : double height
^^^ : 2x size
^^^^ : 3x size
^^^^^ : 4x size
^^^^^^ : 5x size
^^^^^^^ : 6x size`,
  },
  {
    title: "Escape characters in text",
    body: `\\\\  : \\
\\|  : |
\\{  : {
\\}  : }
\\-  : - (cancel horizontal rule)
\\=  : = (cancel paper cut)
\\~  : ~
\\_  : _
\\"  : "
\\\`  : \`
\\^  : ^
\\n  : wrap text manually
\\xnn : hexadecimal character code
\\char : ignore`,
  },
  {
    title: "Properties",
    body: `{i|image: base64 png format}
image

{c|code: string}
barcode / 2D code

{o|option: value, value ...}
barcode / 2D code options

{a|align: left|center|right}
line alignment

{w|width: number|*, number|* ...}
column width

{b|border: none|space|line|0-2}
column border

{t|text: wrap|nowrap}
text wrapping

{x|command: string}
device-specific commands

{_|comment: string}
comment`,
  },
  {
    title: "Special characters in property values",
    body: `\\ : character escape
|  : column delimiter
{  : property delimiter (start)
}  : property delimiter (end)
:  : key-value separator
;  : key-value delimiter`,
  },
  {
    title: "Escape characters in property values",
    body: `\\\\  : \\
\\|  : |
\\{  : {
\\}  : }
\\;  : ;
\\n  : new line
\\xnn : hexadecimal character code
\\char : ignore`,
  },
];

const liquidSyntax = `{{ printer.name }}                 variable
{{ order.service_method | upper }}   variable with filter
{% if hasReassignment %}...{% endif %} conditional
{% for group in groups %}...{% endfor %} loop`;

const kitchenPreviewData = `printer.name
printer.categories[]

order.id
order.display_id
order.service_method
order.is_multicourse
order.table.name
order.party.name
order.items[]

orders[]
restaurant.name
assignedFromOrders[]
hasReassignment
reassignmentInfo

display.date
display.time
display.multicourse
display.orderDisplayName
display.tableInfo
display.covers
display.printJobId

groups[].name
groups[].type
groups[].typeLabel
groups[].items[].quantity
groups[].items[].description
groups[].items[].orderItem.product.name
groups[].items[].orderItem.notes`;

const taxReceiptPreviewData = `printerName

restaurant.address
restaurant.receipt_phone
restaurant.company_registration
restaurant.receipt_message

payment.created_at
payment.method
payment.table.name
payment.discount.category
payment.discount.type
payment.discount.amount
payment.discount.net
payment.tips
payment.amount
payment.split_payment
payment.rewards.total
payment.delivery_fee
payment.guid
payment.pickup_info.name
payment.pickup_info.email
payment.pickup_info.phone
payment.delivery_info.name
payment.delivery_info.company_name
payment.delivery_info.street
payment.delivery_info.city
payment.delivery_info.postal_code
payment.delivery_info.email
payment.delivery_info.phone
payment.delivery_info.notes

orderDisplayNumbers[]

items[].quantity
items[].taxLabel
items[].total
items[].description
items[].notes
items[].product.name
items[].bundles[].display.title

pickup_info.name
pickup_info.email
pickup_info.phone

delivery_info.name
delivery_info.company_name
delivery_info.street
delivery_info.city
delivery_info.postal_code
delivery_info.email
delivery_info.phone
delivery_info.notes`;

const kitchenExample = `^^^{{ printer.name }}
{% unless order.service_method == "dine-in" %}\`^^^{{ order.service_method | upper }}{% endunless %}

|{{ display.date }} ||| {{ display.time }}

{% for group in groups %}
|"{{ group.name }}{% if group.type %} | \\\`^^^ \\\`{{ group.typeLabel | upper }}{% endif %}
{% for item in group.items %}
|{{ item.quantity }} × {{ item.description }}
{% endfor %}
{% endfor %}

-------------------------------------
{{ display.printJobId }}`;

const taxReceiptExample = `|{{paymentDate}} ||| {{paymentTime}}
|"PAIEMENT: "{{paymentMethod}}
|"COMMANDE: {{orderDisplayNumbers}}

{{#items}}
{{description}} | {{quantity}} | {{total}} | {{taxLabel}}|
{{/items}}

^TOTAL | "CHF {{grandTotal}}`;

export const PrintTemplateHelpGuide = defineComponent({
  name: "PrintTemplateHelpGuide",
  props: {
    currentType: {
      type: String as PropType<PrintTemplateType>,
      required: true,
    },
  },
  setup(props) {
    const auth = useAuth();
    return () => {
      const previewData =
        props.currentType === "tax_receipt"
          ? taxReceiptPreviewData
          : kitchenPreviewData;
      const currency = auth.user.value?.restaurant.currency || "CHF";
      const example =
        props.currentType === "tax_receipt"
          ? taxReceiptExample.replace("CHF", currency)
          : kitchenExample;

      return (
        <details class="mt-4 rounded-2xl border  bg-base-100">
          <summary class="cursor-pointer list-none px-4 py-3 text-sm font-medium text-accent">
            Help guide: template syntax and preview variables
          </summary>
          <div class="space-y-4 border-t  px-4 py-4 text-sm text-base-content/70">
            <div>
              <p class="font-medium text-base-content">Receiptline syntax</p>
              <div class="mt-3 space-y-4">
                {receiptlineSections.map((section) => (
                  <div key={section.title}>
                    <p class="text-xs font-semibold uppercase tracking-[0.14em] text-base-content/50">
                      {section.title}
                    </p>
                    <pre class="mt-2 overflow-x-auto rounded-xl bg-base-200 px-3 py-3 text-xs text-base-content">
                      {section.body}
                    </pre>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p class="font-medium text-base-content">
                {props.currentType === "tax_receipt"
                  ? "Mustache syntax"
                  : "Liquid syntax"}
              </p>
              <pre class="mt-2 overflow-x-auto rounded-xl bg-base-200 px-3 py-3 text-xs text-base-content">
                {props.currentType === "tax_receipt"
                  ? `{{name}}                 variable
{{{name}}}               unescaped variable
{{#groups}}...{{/groups}} loop / section
{{#showServiceMethod}}...{{/showServiceMethod}} conditional section`
                  : liquidSyntax}
              </pre>
            </div>

            <div>
              <p class="font-medium text-base-content">
                Preview data for{" "}
                {props.currentType === "tax_receipt"
                  ? "tax receipts"
                  : "kitchen tickets"}
              </p>
              <pre class="mt-2 overflow-x-auto rounded-xl bg-base-200 px-3 py-3 text-xs text-base-content">
                {previewData}
              </pre>
            </div>

            <div>
              <p class="font-medium text-base-content">Example</p>
              <pre class="mt-2 overflow-x-auto rounded-xl bg-base-200 px-3 py-3 text-xs text-base-content">
                {example}
              </pre>
            </div>
          </div>
        </details>
      );
    };
  },
});
